import React, { useState } from 'react';

import { Button, Card, CardActions, CardContent, FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import config from '../../hoa-config';
import { GetApp } from '@mui/icons-material';
import datastore from '../../store/hoa-datastore';
import LoadingButton from '@mui/lab/LoadingButton';
import CustomDateField from '../../Container/custom-date-field';
import dashboardServices from '../../services/hoa-dashboard-api-service';
import InvoiceApiServices from '../../services/hoa-invoice-api-services';

export default function InvoicingDashboard() {
	const [isLoading, setIsLoading] = useState(false);
	const [orgId, setOrgId] = useState('');
	const [orgName, setOrgName] = useState('');
	const [dateRange, setDateRange] = useState({ fromDate: '', toDate: '' });
	const [error, setError] = useState<any>({});

	const onChangeDate = (event: any) => {
		if (event?.date?.value) {
			setDateRange({
				...dateRange,
				[event.date.key]: new Date(event.date.value).toLocaleString('en-US'),
			});
		}
	};

	const downloadCSV = async (descriptions: any[], fileName: string) => {
		const fileExtension = '.xlsx';
		const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

		let header = [
			'Billing Item',
			'Type',
			'Mail/Email Type',
			'Organization',
			'Property Address',
			'External ID',
			'Property ID',
			'HOA',
			'HOA Address',
			'HOA ID',
			'City',
			'Market',
			'Onboarding Date',
			'Source ID',
			'Entry ID',
			"Linked Entry ID",
			'Billing Amount',
			'Timestamp',
			'Last Billable HOA Update',
			'Last Billable Property Update',
			'Remarks',
			'Events',
			'User',
		];

		header.push(...Array.from({ length: 300 }).flatMap((_, i) => [
			`Group-${i + 1}`,
			`Sub Group-${i + 1}`,
			`Type-${i + 1}`
		]));

		const ws: any = XLSX.utils.book_new();
		ws['!cols'] = header.map(() => ({ width: 25 }));
		ws['!rows'] = descriptions.map(() => ({ hpx: 20 }));

		XLSX.utils.sheet_add_aoa(ws, [header], { origin: 'A1' });

		XLSX.utils.sheet_add_json(ws, descriptions, { origin: 'A2', skipHeader: true });

		descriptions.forEach((desc, rowIndex) => {
			const cellAddress = `H${rowIndex + 2}`; // +2 because header is row 1
			const hoaName = desc.hoa;
			const hoaLink = `https://www.insidemaps.com/app/hoa/?menu=hoas&id=${desc.HoaId}&tab=overview`;
		if (hoaName && hoaName !== 'N/A') {
			if (!ws[cellAddress]) ws[cellAddress] = {};
			ws[cellAddress].l = { Target: hoaLink, Tooltip: "View HOA" };
			ws[cellAddress].t = 's';
			ws[cellAddress].v = hoaName;
		}
		});

		descriptions.forEach((desc, rowIndex) => {
			const cellAddress = `E${rowIndex + 2}`; // +2 because header is row 1
			const propertyAddress = desc.propertyAddress;
			const propertyLink = `https://www.insidemaps.com/app/hoa/?menu=properties&id=${desc.propertyId}&tab=overview`;
		if (propertyAddress && propertyAddress !== 'N/A') {
			if (!ws[cellAddress]) ws[cellAddress] = {};
			ws[cellAddress].l = { Target: propertyLink, Tooltip: "View Property" };
			ws[cellAddress].t = 's';
			ws[cellAddress].v = propertyAddress;
		}
		});


		const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
		const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
		const finalData = new Blob([excelBuffer], { type: fileType });
		saveAs(finalData, fileName + fileExtension);
	};

	const exportCSV = async () => {
		if (checkDateError()) {
			return;
		}
		if (new Date(dateRange.fromDate) > new Date(dateRange.toDate)) {
			alert('Date Formate incorrect !! From Selected date should be smaller than the To selected Date');
			return;
		}

		let totalDescription: any = [];
		const invoicesCountResponse = await dashboardServices.getSelectedInvoicesCountV2(orgId, dateRange.fromDate, dateRange.toDate);
		const dataJson = await InvoiceApiServices.getInvoiceDataConfiguratorByOrganization(orgId);

		if (invoicesCountResponse?.data?.logCount > 0) {
			for (let page = 1; page <= Math.ceil(invoicesCountResponse.data.logCount / 1000); page++) {
				const descriptions = await getSelectedInvoices(page, dataJson.data);

				for (let description of descriptions) {
					totalDescription.push(description);
				}
			}
		}

		const fileName = `Invoice-${orgName}(Organization ID: ${orgId}), From ${dateRange.fromDate.split(',')[0]} To ${
			dateRange.toDate.split(',')[0]
		}`;
		downloadCSV(totalDescription, fileName);
	};

	const getSelectedInvoices = async (page: number, invoiceData:any) => {
		setIsLoading(true);
		const invoices = await dashboardServices.getSelectedInvoicesV2(orgId, dateRange.fromDate, dateRange.toDate, page, 1000, orgName, invoiceData);
		if (invoices) {
			setIsLoading(false);
		}
		return invoices.data;
	};

	const checkDateError = () => {
		const error: any = {};
		let isEmpty = false;

		if (!dateRange.fromDate) {
			error['fromDate'] = true;
			isEmpty = true;
		}
		if (!dateRange.toDate) {
			error['toDate'] = true;
			isEmpty = true;
		}
		if (!orgId) {
			error['orgId'] = true;
			isEmpty = true;
		}

		setError(error);
		return isEmpty;
	};

	const handleOrganizationSelection = (event: SelectChangeEvent) => {
		const selectedData = event.target.value as any;
		setOrgId(selectedData);
		const orgName = config.hoaOrganizations.find((org: any) => org.id === selectedData)?.name;
		setOrgName(orgName);
	};

	return (
		<>
			<h1 style={{ marginLeft: '24px' }}>Invoicing</h1>
			<Card style={{ width: 450, margin: '168px auto' }}>
				<CardContent style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '18px', flexDirection: 'column' }}>
					<FormControl sx={{ width: 300 }}>
						<InputLabel id='organization-select-label'>Organization</InputLabel>
						<Select
							labelId='organization-select-label'
							id='organization-select'
							label='Organization'
							value={orgId || ''}
							onChange={handleOrganizationSelection}
							error={error['orgId'] || false}
						>
							{config.hoaOrganizations.map((org: any) => {
								return (
									<MenuItem key={org.id} value={org.id}>
										{org.name}
									</MenuItem>
								);
							})}
						</Select>
						<FormHelperText error={error['orgId'] || false}>{error['orgId'] ? 'Please select an organization' : ''}</FormHelperText>
					</FormControl>

					<CustomDateField
						label='From'
						error={error['fromDate'] || false}
						variant='filled'
						style={{ width: '300px' }}
						value={dayjs(dateRange.fromDate || '')}
						handleChange={(value: any) => onChangeDate({ date: { key: 'fromDate', value: value } })}
						isClearable={{ clearable: true, onClear: () => setDateRange({ ...dateRange, fromDate: '' }) }}
						helperText={error['fromDate'] ? 'fill up the date' : ''}
					/>

					<CustomDateField
						label='Date'
						error={error['toDate'] || false}
						variant='filled'
						style={{ width: '300px' }}
						value={dayjs(dateRange.toDate || '')}
						handleChange={(value: any) => {
							onChangeDate({ date: { key: 'toDate', value: value } });
						}}
						isClearable={{ clearable: true, onClear: () => setDateRange({ ...dateRange, toDate: '' }) }}
						helperText={error['toDate'] ? 'fill up the date' : ''}
					/>
				</CardContent>

				<CardActions style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					{datastore.isUserPermitted('Invoicing', '', 'download') &&
						(!isLoading ? (
							<Button
								onClick={exportCSV}
								size='small'
								style={{ backgroundColor: '#3576E5', color: '#FFF', width: 'auto', padding: '14px 12px' }}
								startIcon={<GetApp style={{ color: '#fff' }} />}
							>
								Download
							</Button>
						) : (
							<LoadingButton loading loadingIndicator='Downloading...' variant='outlined'>
								Downloading Data
							</LoadingButton>
						))}
				</CardActions>
			</Card>
		</>
	);
}
